"use client";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Container,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Image from "next/image";
import getBasicPlanPrice from "@/utils/getBasicPlanPrice";
import getUserRegion from "@/utils/getUserRegion";
import ScrollReveal from "./ScrollReveal";
import { IMAGES } from "@/constants/images";
import { useTranslation } from "react-i18next";

export default function UrgencyBanner({ onClaimClick }) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [spotsRemaining, setSpotsRemaining] = useState(43);
  const [totalSpots, setTotalSpots] = useState(50);
  const progress = ((totalSpots - spotsRemaining) / totalSpots) * 100;

  const [basicPlanPrice, setBasicPlanPrice] = useState(29);
  const [currency, setCurrency] = useState("USD");
  const [founderPrice, setFounderPrice] = useState(247);
  const [founderCurrency, setFounderCurrency] = useState("USD");

  const formatNumber = (num) =>
    num ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : num;

  const getFounderPricing = (country) => {
    const pricing = {
      PK: { price: 23000, currency: "PKR" },
      AE: { price: 79, currency: "USD" },
      US: { price: 247, currency: "USD" },
    };
    return pricing[country] || { price: 247, currency: "USD" };
  };

  const readOffersData = () => {
    try {
      const offersData = localStorage.getItem("offersData");
      if (offersData) {
        const parsedData = JSON.parse(offersData);
        const spots = parsedData?.data?.remainingSpots;
        const allSpots = parsedData?.data?.allSpots;
        if (spots !== undefined && spots !== null) setSpotsRemaining(spots);
        if (allSpots !== undefined && allSpots !== null)
          setTotalSpots(allSpots);
      }
    } catch (error) {
      console.error("Error reading remainingSpots:", error);
    }
  };

  useEffect(() => {
    const fetchBasicPlanPrice = async () => {
      try {
        const userRegionData = localStorage.getItem("userRegion");
        if (!userRegionData) await getUserRegion();
        const data = await getBasicPlanPrice();
        if (data) {
          setBasicPlanPrice(data?.amount);
          setCurrency(data?.currency);
        }
        if (userRegionData) {
          const userRegion = JSON.parse(userRegionData);
          const founderPricing = getFounderPricing(userRegion.country);
          setFounderPrice(founderPricing.price);
          setFounderCurrency(founderPricing.currency);
        }
      } catch (error) {
        console.error("UrgencyBanner: Error fetching price:", error);
      }
    };
    fetchBasicPlanPrice();
  }, []);

  useEffect(() => {
    readOffersData();
    const interval = setInterval(() => {
      readOffersData();
    }, 1000);
    const handleStorageChange = (e) => {
      if (e.key === "offersData") readOffersData();
    };
    const handleCustomStorageEvent = () => {
      readOffersData();
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomStorageEvent);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "localStorageUpdated",
        handleCustomStorageEvent,
      );
    };
  }, []);

  const badges = [
    t("urgencyBanner.badge1"),
    t("urgencyBanner.badge2"),
    t("urgencyBanner.badge3"),
  ];

  return (
    <ScrollReveal delay={0.1} duration={1.2}>
      <Box
        sx={{
          background: "linear-gradient(to right, #DC2626 0%, #B91C1C 100%)",
          py: { xs: 4, sm: 5 },
          px: 2,
          color: "white",
          direction: isArabic ? "rtl" : "ltr",
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.5,
              maxWidth: 900,
              mx: "auto",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: { xs: 0, sm: 1 },
                justifyContent: "center",
                mt: "2%",
                mb: "1%",
                px: { xs: 0, sm: 2 },
              }}
            >
              <Image
                src={IMAGES.SPARK_IMG}
                alt="Spark Icon"
                width={28}
                height={28}
                style={{
                  objectFit: "contain",
                  flexShrink: 0,
                  marginTop: "10px",
                }}
              />
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.3rem", sm: "1.9rem" },
                  fontFamily: "var(--font-plusjakarta)",
                  lineHeight: 1.5,
                  textAlign: "center",
                }}
              >
                {t("urgencyBanner.title")}
              </Typography>
            </Box>

            {/* Spots Remaining */}
            <Box
              sx={{
                backgroundColor: "#FFFFFF1A",
                borderRadius: 4,
                p: { xs: 2.5, sm: 3.5 },
                width: { xs: "100%", sm: "80%" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "2.7rem", sm: "3rem" },
                  lineHeight: 1,
                  mb: 1,
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {spotsRemaining}
              </Typography>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 400,
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {t("urgencyBanner.spotsRemaining")}
              </Typography>
              <Box
                sx={{
                  width: { xs: "80%", sm: "50%" },
                  mb: 1.5,
                  mx: "auto",
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 10,
                    borderRadius: 8,
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#FACC15",
                      borderRadius: 8,
                    },
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {t("urgencyBanner.outOf", { totalSpots })}
              </Typography>
            </Box>

            {/* Warning */}
            <Box
              sx={{
                backgroundColor: "#cc3d1a",
                borderRadius: 4,
                p: { xs: 2, sm: 2.5 },
                width: { xs: "100%", sm: "80%" },
                border: "1px solid #cc3d1a",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  mb: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Image
                  src={IMAGES.BELL_ICON_IMG}
                  alt="Warning Icon"
                  width={20}
                  height={20}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1rem", sm: "1.2rem" },
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {t("urgencyBanner.warningTitle")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  lineHeight: 1.5,
                  fontFamily: "var(--font-outfit)",
                  maxWidth: "93%",
                  textAlign: "center",
                  mx: "auto",
                }}
              >
                {t("urgencyBanner.warningDesc", { totalSpots })}{" "}
                <span style={{ color: "#FFDD2C" }}>
                  {t("urgencyBanner.currentPricing", {
                    founderCurrency,
                    founderPrice: formatNumber(founderPrice),
                  })}
                </span>{" "}
                {t("urgencyBanner.publicPricing", {
                  currency,
                  basicPlanPrice: formatNumber(basicPlanPrice),
                })}
              </Typography>
            </Box>

            {/* Button */}
            <Button
              variant="contained"
              size="large"
              onClick={onClaimClick}
              sx={{
                backgroundColor: "white",
                color: "#DC2626",
                textTransform: "none",
                px: 3.5,
                py: 1.25,
                fontSize: { xs: "0.7rem", sm: "0.9rem" },
                fontWeight: 700,
                borderRadius: "6px",
                alignSelf: "center",
                fontFamily: "var(--font-outfit)",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                  transform: "scale(1.05) translateY(-2px)",
                },
              }}
              endIcon={
                <ArrowForwardIcon
                  sx={{
                    fontSize: "1rem",
                    transform: isArabic ? "scaleX(-1)" : "none",
                  }}
                />
              }
            >
              {t("urgencyBanner.cta")}
            </Button>

            {/* Trust Badges */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                gap: 1.5,
                mt: 0.5,
              }}
            >
              {badges.map((text, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Image
                    src={IMAGES.CHECKICON_WITH_BG}
                    alt="Check"
                    width={16}
                    height={16}
                    style={{ objectFit: "contain" }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </ScrollReveal>
  );
}
